import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Stack,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Person as PersonIcon,
  AccountCircle as UserIcon,
  LinkOff as UnlinkIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { PersonLinkService } from '../../services/personLinkService';
import { PersonLinkRequest, LinkRequestStats } from '../../types/personLink';

/**
 * Admin panel for reviewing person link requests
 */
const LinkRequestsPanel: React.FC = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const user = authState.user;

  const [currentTab, setCurrentTab] = useState(0);
  const [requests, setRequests] = useState<PersonLinkRequest[]>([]);
  const [stats, setStats] = useState<LinkRequestStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Review dialog state
  const [reviewDialog, setReviewDialog] = useState<{
    open: boolean;
    request: PersonLinkRequest | null;
    action: 'approve' | 'reject' | null;
  }>({ open: false, request: null, action: null });
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewing, setReviewing] = useState(false);

  // Unlink dialog state
  const [unlinkDialog, setUnlinkDialog] = useState<{
    open: boolean;
    request: PersonLinkRequest | null;
  }>({ open: false, request: null });
  const [unlinking, setUnlinking] = useState(false);

  // Load requests and stats
  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [allRequests, statsData] = await Promise.all([
        PersonLinkService.getAllRequests(),
        PersonLinkService.getStats(),
      ]);

      setRequests(allRequests);
      setStats(statsData);
    } catch (err: any) {
      console.error('Error loading link requests:', err);
      setError(err.message || 'Failed to load link requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Filter requests by tab
  const filteredRequests = requests.filter(request => {
    switch (currentTab) {
      case 0: // All
        return true;
      case 1: // Pending
        return request.status === 'pending';
      case 2: // Approved
        return request.status === 'approved';
      case 3: // Rejected
        return request.status === 'rejected';
      default:
        return true;
    }
  });

  // Open review dialog
  const handleReview = (request: PersonLinkRequest, action: 'approve' | 'reject') => {
    setReviewDialog({ open: true, request, action });
    setReviewNotes('');
  };

  // Close review dialog
  const handleCloseReview = () => {
    setReviewDialog({ open: false, request: null, action: null });
    setReviewNotes('');
  };

  // Submit review
  const handleSubmitReview = async () => {
    if (!reviewDialog.request || !reviewDialog.action || !user) return;

    setReviewing(true);

    try {
      const reviewData = {
        requestId: reviewDialog.request.id,
        action: reviewDialog.action,
        reviewedBy: user.uid,
        reviewedByName: user.displayName,
        reviewNotes,
      };

      if (reviewDialog.action === 'approve') {
        await PersonLinkService.approveRequest(reviewData);
      } else {
        await PersonLinkService.rejectRequest(reviewData);
      }

      // Reload data
      await loadData();

      // Close dialog
      handleCloseReview();
    } catch (err: any) {
      console.error('Error reviewing request:', err);
      setError(err.message || 'Failed to review request');
    } finally {
      setReviewing(false);
    }
  };

  // Open unlink dialog
  const handleUnlink = (request: PersonLinkRequest) => {
    setUnlinkDialog({ open: true, request });
  };

  // Close unlink dialog
  const handleCloseUnlink = () => {
    setUnlinkDialog({ open: false, request: null });
  };

  // Submit unlink
  const handleSubmitUnlink = async () => {
    if (!unlinkDialog.request || !user) return;

    setUnlinking(true);

    try {
      await PersonLinkService.unlinkUser(
        unlinkDialog.request.userId,
        user.uid,
        user.displayName || 'Unknown Admin',
        'Unlinked from admin panel'
      );

      // Reload data
      await loadData();

      // Close dialog
      handleCloseUnlink();
    } catch (err: any) {
      console.error('Error unlinking user:', err);
      setError(err.message || 'Failed to unlink user');
    } finally {
      setUnlinking(false);
    }
  };

  // Format date
  const formatDate = (date: Date): string => {
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/admin')}
        sx={{ mb: 3 }}
      >
        Back to Dashboard
      </Button>

      <Typography variant="h4" gutterBottom>
        Person Link Requests
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Review requests from users to link their account to a family tree profile
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Statistics */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4">{stats.total}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Requests
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="warning.main">
                  {stats.pending}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pending Review
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="success.main">
                  {stats.approved}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Approved
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="error.main">
                  {stats.rejected}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Rejected
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={currentTab} onChange={(_, val) => setCurrentTab(val)}>
          <Tab label={`All (${requests.length})`} />
          <Tab label={`Pending (${requests.filter(r => r.status === 'pending').length})`} />
          <Tab label={`Approved (${requests.filter(r => r.status === 'approved').length})`} />
          <Tab label={`Rejected (${requests.filter(r => r.status === 'rejected').length})`} />
        </Tabs>
      </Paper>

      {/* Request List */}
      {filteredRequests.length === 0 ? (
        <Alert severity="info">
          No {currentTab === 1 ? 'pending ' : currentTab === 2 ? 'approved ' : currentTab === 3 ? 'rejected ' : ''}
          requests found.
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {filteredRequests.map(request => (
            <Grid item xs={12} md={6} lg={4} key={request.id}>
              <Card>
                <CardContent>
                  {/* Status chip */}
                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label={request.status.toUpperCase()}
                      color={
                        request.status === 'pending'
                          ? 'warning'
                          : request.status === 'approved'
                          ? 'success'
                          : 'error'
                      }
                      size="small"
                    />
                  </Box>

                  {/* User Info */}
                  <Box sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <UserIcon color="action" />
                      <Typography variant="subtitle2" color="text.secondary">
                        User Account
                      </Typography>
                    </Stack>
                    <Typography variant="body1" fontWeight="medium">
                      {request.userName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {request.userEmail}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Person Info */}
                  <Box sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <PersonIcon color="action" />
                      <Typography variant="subtitle2" color="text.secondary">
                        Family Tree Profile
                      </Typography>
                    </Stack>
                    <Typography variant="body1" fontWeight="medium">
                      {request.personName}
                    </Typography>
                  </Box>

                  {/* Dates */}
                  <Typography variant="caption" display="block" color="text.secondary">
                    Requested: {formatDate(request.requestedAt)}
                  </Typography>

                  {request.reviewedAt && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      Reviewed: {formatDate(request.reviewedAt)}
                    </Typography>
                  )}

                  {/* Review notes */}
                  {request.reviewNotes && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="caption">
                        <strong>Notes:</strong> {request.reviewNotes}
                      </Typography>
                    </Alert>
                  )}
                </CardContent>

                {/* Actions */}
                {request.status === 'pending' && (
                  <CardActions>
                    <Button
                      size="small"
                      color="success"
                      startIcon={<ApproveIcon />}
                      onClick={() => handleReview(request, 'approve')}
                    >
                      Approve
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<RejectIcon />}
                      onClick={() => handleReview(request, 'reject')}
                    >
                      Reject
                    </Button>
                  </CardActions>
                )}

                {/* Unlink action (only for approved) */}
                {request.status === 'approved' && (
                  <CardActions>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<UnlinkIcon />}
                      onClick={() => handleUnlink(request)}
                    >
                      Unlink User
                    </Button>
                  </CardActions>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Review Dialog */}
      <Dialog
        open={reviewDialog.open}
        onClose={() => !reviewing && handleCloseReview()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {reviewDialog.action === 'approve' ? 'Approve' : 'Reject'} Link Request
        </DialogTitle>

        <DialogContent>
          {reviewDialog.request && (
            <>
              <Typography paragraph>
                {reviewDialog.action === 'approve'
                  ? `Approve linking ${reviewDialog.request.userName} to ${reviewDialog.request.personName}?`
                  : `Reject linking ${reviewDialog.request.userName} to ${reviewDialog.request.personName}?`}
              </Typography>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes (optional)"
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder={
                  reviewDialog.action === 'reject'
                    ? 'Provide a reason for rejection...'
                    : 'Add any notes...'
                }
              />
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseReview} disabled={reviewing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={reviewDialog.action === 'approve' ? 'success' : 'error'}
            onClick={handleSubmitReview}
            disabled={reviewing}
            startIcon={reviewing ? <CircularProgress size={16} /> : null}
          >
            {reviewDialog.action === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unlink Dialog */}
      <Dialog
        open={unlinkDialog.open}
        onClose={() => !unlinking && handleCloseUnlink()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Unlink User from Person</DialogTitle>

        <DialogContent>
          {unlinkDialog.request && (
            <>
              <Typography paragraph>
                Are you sure you want to unlink {unlinkDialog.request.userName} from{' '}
                {unlinkDialog.request.personName}?
              </Typography>

              <Alert severity="warning" sx={{ mb: 2 }}>
                This will remove the connection between the user account and the family tree
                profile. The user can submit a new link request if needed.
              </Alert>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseUnlink} disabled={unlinking}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleSubmitUnlink}
            disabled={unlinking}
            startIcon={unlinking ? <CircularProgress size={16} /> : <UnlinkIcon />}
          >
            Unlink
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LinkRequestsPanel;
